import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Dialog,
  IconButton,
  FormControlLabel,
  Switch,
  Popover,
  DialogActions,
} from "@mui/material";
import styles from "./Coupons.module.css";
import Autocomplete from "@mui/material/Autocomplete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import EditIcon from "@mui/icons-material/Edit";
import {
  useLoaderContext,
  useToastContext,
} from "../../../Context/ToastContext";
import { useUserContext } from "../../../Context/UserContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dropdown from "../Dropdown/Dropdown";
import { Helmet } from "react-helmet";
import { apiGet, apiPost, apiPut } from "../../../utils/ApiManager";
import "dayjs/locale/en-gb";
dayjs.locale("en-gb");


const fetchAllCoupons = process.env.REACT_APP_FETCH_ALL_COUPONS;
const fetchRestaurantsBooking =
  process.env.REACT_APP_FETCH_RESTAURANTS_VIEW_BOOKING;
const addNewCoupon = process.env.REACT_APP_ADD_NEW_COUPON;
const getupdateCoupon = process.env.REACT_APP_UPDATE_COUPON;
const getToggleActivation = process.env.REACT_APP_TOGGLE_COUPON_ACTIVATION;

const Coupons = () => {
  const { showToast } = useToastContext();
  const { setLoading } = useLoaderContext();
  const { requestConfirmation, getToken } = useUserContext();
  const [coupons, setCoupons] = useState([]);
  const [restaurantList, setRestaurantList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    couponCode: "",
    percentage: "",
    title: "",
    maxAmount: "",
    validFrom: null,
    validTill: null,
    restaurants: [],
    usageLimit: "",
    type: "",
    minCartValue: "",
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [couponTypes, setCouponTypes] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      
      const response = await apiGet(fetchAllCoupons);
      if (response?.success) {
        setCoupons(response?.data || []);
      } else {
        showToast(
          response?.message || "Failed to fetch coupons !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Failed to fetch coupons !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);

        const response = await apiGet(`${fetchRestaurantsBooking}?couponTypes=true`);
        if (response?.success) {
          const restarants = response.data?.restaurantList
            ? [
                { label: "Select All", value: "all" },
                ...response.data?.restaurantList,
              ]
            : [];
          const couponTypes = response.data?.couponTypes || [];
          setRestaurantList(restarants);
          setCouponTypes(couponTypes);
        } else {
          showToast(
            response?.message || "Failed to fetch restaurants !",
            "error"
          );
        }
      } catch (error) {
        showToast(
          error?.response?.message ||
            error?.message ||
            "Failed to fetch restaurants !",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
    fetchRestaurants();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddCouponClick = () => {
    setFormData({
      couponCode: "",
      percentage: "",
      maxAmount: "",
      validFrom: null,
      validTill: null,
      restaurants: [],
      usageLimit: "",
      minCartValue: "",
    });
    setIsEditing(false);
    setOpenModal(true);
  };

  const createCoupon = async () => {
    if (!validateForm()) return;

    const payload = {
      ...formData,
      restaurants: formData.restaurants?.map((obj) => obj?.value),
    };

    try {
      const result = await requestConfirmation(
        `Want to create new Coupon - ${formData?.couponCode} ?`
      );
      if (!result) return;

      setLoading(true);
      
      const response = await apiPost(addNewCoupon, payload);

      if (response?.success) {
        showToast(
          response?.message || "Coupon created successfully!",
          "success"
        );
        setOpenModal(false);
        setIsEditing(false);
        fetchCoupons();
        setFormData({
          couponCode: "",
          percentage: "",
          maxAmount: "",
          validFrom: null,
          validTill: null,
          restaurants: [],
          usageLimit: "",
          minCartValue: "",
        });
      } else {
        showToast(
          response?.message || "Failed to create coupon!",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Failed to create coupon!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formData]);

  const updateCoupon = async () => {
    try {
      if (!validateForm()) return; // validateForm handles error messages and validation
      const result = await requestConfirmation(
        `Want to edit coupon - ${formData?.couponCode} ?`
      );
      if (!result) return;
      setLoading(true);
      const valueList = formData.restaurants?.map((obj) => obj?.value);
      formData.restaurants = valueList;
      const response = await apiPut(`${getupdateCoupon}`, formData);
    
      if (response?.success) {
        setOpenModal(false);
        showToast(
          response?.message || "Coupon edit successfully !",
          "success"
        );
        fetchCoupons();
        setIsEditing(false);
      } else {
        showToast(
          response?.message || "Failed to edit coupon !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Something went wrong!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    const requiredFields = [
      "couponCode",
      "title",
      "percentage",
      "maxAmount",
      "restaurants",
      "validFrom",
      "validTill",
      "usageLimit",
      "type",
      "minCartValue",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
        isValid = false;
      }
    });

    // Coupon Code
    if (formData.couponCode.trim().length > 15) {
      newErrors.couponCode = "Coupon code must be less than 15 characters";
      isValid = false;
    }

    // Description
    if (
      formData?.title &&
      (formData?.title?.length > 30 || formData?.title?.length < 1)
    ) {
      newErrors.title = "Description must be less than 30 characters";
      isValid = false;
    }

    // Percentage
    if (!formData.percentage) {
      newErrors.percentage = "Discount percentage is required";
      isValid = false;
    } else if (
      isNaN(formData.percentage) ||
      formData.percentage <= 0 ||
      formData.percentage > 100
    ) {
      newErrors.percentage = "Discount must be a number between 1 and 100";
      isValid = false;
    }

    // Max Discount Amount
    if (!formData.maxAmount) {
      newErrors.maxAmount = "Maximum discount amount is required";
      isValid = false;
    } else if (isNaN(formData.maxAmount) || formData.maxAmount <= 0) {
      newErrors.maxAmount = "Maximum discount must be a positive number";
      isValid = false;
    }

    // Restaurants
    if (
      !Array.isArray(formData.restaurants) ||
      formData.restaurants.length === 0
    ) {
      newErrors.restaurants = "Please select at least one restaurant";
      isValid = false;
    }

    // MinCartValue
    if (formData.minCartValue < 400) {
      newErrors.minCartValue = "Minimum cart value must be at least ₹400";
      isValid = false;
    }

    // Usage Limit
    if (!formData.usageLimit) {
      newErrors.usageLimit = "Usage limit is required";
      isValid = false;
    } else if (isNaN(formData.usageLimit) || formData.usageLimit <= 0) {
      newErrors.usageLimit = "Usage limit must be a positive number";
      isValid = false;
    }

    // Valid From
    if (!formData.validFrom || !dayjs(formData.validFrom).isValid()) {
      newErrors.validFrom = "This field is required";
      isValid = false;
    } else if (dayjs(formData.validFrom).isBefore(dayjs(), "day")) {
      newErrors.validFrom = "Valid From date cannot be in the past";
      isValid = false;
    }

    // Valid Till
    if (!formData.validTill || !dayjs(formData.validTill).isValid()) {
      newErrors.validTill = "This field is required";
      isValid = false;
    } else if (
      dayjs(formData.validFrom).isValid() &&
      dayjs(formData.validTill).isBefore(dayjs(formData.validFrom), "day")
    ) {
      newErrors.validTill = "End date must be after start date";
      isValid = false;
    }

    setErrors(newErrors);
    setIsFormValid(isValid);
    return isValid;
  };

  const handleValidFromChange = (newDate) => {
    if(!dayjs(newDate).isValid()){
      return
    }
    setFormData((prev) => ({
      ...prev,
      validFrom: newDate, // Store Dayjs object
    }));
  };

  // Handle "Valid Till" date change
  const handleValidTillChange = (newDate) => {
    if(!dayjs(newDate).isValid()){
      return
    }
    setFormData((prev) => ({
      ...prev,
      validTill: newDate, // Store Dayjs object
    }));
  };

  useEffect(() => {
    validateForm();
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    isEditing ? updateCoupon() : createCoupon();
  };

  const toggleCouponActivation = async (couponCode, isActive) => {
    try {
      if (!couponCode) return;
      const couponData = coupons.find((coupon) => coupon.couponCode === couponCode);
      if (isActive) {
        if (
          // new Date() > new Date(couponData?.validFrom) ||
          new Date() > new Date(couponData?.validTill)
        ) {
          showToast("Please Update Coupon Dates !", "error");
          return;
        }
      }
      const result = await requestConfirmation(
        `Want to ${
          isActive ? "activate" : "deactivate"
        } coupon - '${couponCode}' ?`
      );
      if (!result) return;
      setLoading(true);
      

      const response = await apiPut(getToggleActivation, { couponCode, isActive });
      if (response?.success) {
        const updated = coupons.map((coupon) =>
          coupon.couponCode === couponCode ? { ...coupon, isActive } : coupon
        );
        setCoupons([...updated]);
        fetchCoupons();
        showToast(
          response?.message ||
            `Coupon ${isActive ? "activated" : "deactivated"} successfully !`,
          "success"
        );
      } else {
        showToast(
          response?.message ||
            `Failed to ${isActive ? "activate" : "deactivate"} coupon !`,
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          `Failed to ${isActive ? "activate" : "deactivate"} coupon !`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (couponData) => {
    const newCouponData = {
      ...couponData,
      validFrom: dayjs(couponData?.validFrom),
      validTill: dayjs(couponData?.validTill),
    };
    const selected = restaurantList?.filter((rest) =>
      couponData?.restaurants?.some((x) => x?.restaurantId === rest?.value)
    );
    newCouponData.restaurants = selected;
    setFormData(newCouponData);
    setOpenModal(true);
    setIsEditing(true);
  };

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Coupons | DiNEST</title>
        <meta name="description" content="View coupons on DiNEST" />
      </Helmet>

      <Typography variant="h4" className={styles.containerTitle}>
        Coupons and Offers
      </Typography>

      <Box className={styles.buttonContainer}>
        <Button
          className={styles.couponButton}
          variant="contained"
          onClick={handleAddCouponClick}
        >
          Create Coupon
        </Button>
      </Box>

      {coupons?.length > 0 ? (
        <Box className={styles.existingCouponsContainer}>
          {/* <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
            Your Coupons
          </Typography> */}

          <Box container spacing={5} className={styles.innerGridParent}>
            {coupons.map((coupon) => (
              <Grid key={coupon._id} className={styles.cardsContainer}>
                <Card
                  className={`${styles.card} ${
                    coupon.isActive ? styles.activeCard : styles.inactiveCard
                  }`}
                >
                  <FormControlLabel
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      position: "relative",
                      top: 10,
                      left: 10,
                    }}
                    control={
                      <Tooltip
                        title={coupon.isActive ? "Switch to Deactivate" : "Switch to Activate"}
                        arrow
                      >
                        <Switch 
                        size="small"
                          checked={coupon.isActive}
                          onChange={(e) =>
                            toggleCouponActivation(
                              coupon.couponCode,
                              !coupon.isActive
                            )
                          }
                          sx={{
                            "& .MuiSwitch-thumb": {
                              backgroundColor: coupon.isActive
                                ? "green"
                                : "red",
                            },
                            "& .MuiSwitch-track": {
                              backgroundColor: coupon.isActive
                                ? "lightgreen"
                                : "lightcoral",
                            },
                          }}
                        />
                      </Tooltip>
                    }
                    label=""
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      position: "absolute",
                      bottom: 5,
                      right: 5,
                    }}
                  >
                    <Tooltip title="Edit Coupon" arrow>
                      <IconButton
                        onClick={() => handleEditClick(coupon)}
                        sx={{
                          color: coupon.isActive ? "#207d46" : 'red',
                          // visibility: coupon.isActive ? "visible" : "hidden",
                        }}
                      >
                        <EditIcon fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <CardContent className={styles.cardContent}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        mt: 3,
                        fontWeight: "600",
                        color: coupon.isActive ? "#207d46" : "red",
                      }}
                    >
                      {coupon.couponCode || "N/A"}
                    </Typography>

                    <Tooltip title={coupon.title || "N/A"} arrow>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          cursor: "pointer",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: "100%", // or set a fixed width if needed
                        }}
                      >
                        <strong>Description:</strong> {coupon.title || "N/A"}
                      </Typography>
                    </Tooltip>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <strong>Discount:</strong>{" "}
                      {coupon.percentage ? `${coupon.percentage}%` : "N/A"}
                      {coupon?.maxAmount ? ` (max ₹${coupon.maxAmount})` : ""}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <strong>Valid From:</strong>{" "}
                      {coupon.validFrom
                        ? new Date(coupon.validFrom).toLocaleDateString("en-GB")
                        : "N/A"}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <strong>Valid Till:</strong>{" "}
                      {coupon.validTill
                        ? new Date(coupon.validTill).toLocaleDateString("en-GB")
                        : "N/A"}
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                      <Tooltip arrow
                        title={
                          coupon?.restaurants?.length > 0
                            ? coupon.restaurants
                                .map((r) => r.restaurantName)
                                .join(", ")
                            : "All Restaurants"
                        }
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className={styles.ellipsisText}
                        >
                          <strong>Applicable to:</strong>{" "}
                          {coupon?.restaurants?.length > 0
                            ? coupon.restaurants
                                .map((r) => r.restaurantName)
                                .join(", ")
                            : "All Restaurants"}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* Usage limit */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Usage limit:</strong>{" "}
                        {coupon?.usageLimit ?? "N/A"}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <strong>Minimum Cart Value:</strong> ₹{" "}
                      {coupon?.minCartValue ?? "N/A"}/-
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Box>
        </Box>
      ) : (
        <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
          No coupons yet, you can create one !
        </h2>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="sm"
        className={styles.editContainer}
      >
        <div
          onClick={() => setOpenModal(false)}
          className={styles.backIconContainer}
        >
          <ArrowBackIcon className={styles.backIcon} />
        </div>

        <Typography variant="h5" className={styles.addContainerTitle}>
          {isEditing ? "Edit Coupon" : "Add Coupon"}
        </Typography>

        <form onSubmit={handleSubmit} className={styles.formScrollable}>
          <Grid container spacing={2} justifyContent="center">
            {/* Coupon Code */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <Dropdown
                fullWidth
                name="type"
                options={couponTypes}
                label="Coupon Type"
                value={formData.type}
                onChange={handleChange}
              />
              {errors?.type && <p className={styles.error}>{errors?.type}</p>}
            </Grid>
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="couponCode"
                label="Coupon Code"
                value={formData.couponCode}
                disabled={isEditing}
                onChange={handleChange}
                variant="standard"
                required
              />
              {errors?.couponCode && (
                <p className={styles.error}>{errors?.couponCode}</p>
              )}
            </Grid>
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="title"
                label="Coupon Description"
                value={formData.title}
                onChange={handleChange}
                variant="standard"
                // required
              />
              {errors?.title && <p className={styles.error}>{errors?.title}</p>}
            </Grid>
            {/* Discount */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="percentage"
                label="Discount (%)"
                type="number"
                disabled={isEditing}
                value={formData.percentage}
                onChange={handleChange}
                variant="standard"
                required
                InputProps={{
                  inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                  classes: { input: styles.noArrows },
                }}
              />
              {errors?.percentage && (
                <p className={styles.error}>{errors?.percentage}</p>
              )}
            </Grid>

            {/* Max Amount */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="maxAmount"
                label="Maximum Discount (₹)"
                type="number"
                disabled={isEditing}
                value={formData.maxAmount}
                onChange={handleChange}
                variant="standard"
                required
                InputProps={{
                  inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                  classes: { input: styles.noArrows },
                }}
              />
              {errors?.maxAmount && (
                <p className={styles.error}>{errors?.maxAmount}</p>
              )}
            </Grid>

            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="minCartValue"
                label="Minimum Cart Value (₹)"
                type="number"
                // disabled={isEditing}
                value={formData.minCartValue}
                onChange={handleChange}
                variant="standard"
                required
                InputProps={{
                  inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                  classes: { input: styles.noArrows },
                }}
              />
              {errors?.minCartValue && (
                <p className={styles.error}>{errors?.minCartValue}</p>
              )}
            </Grid>

            {/* Restaurants */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <Autocomplete
                multiple
                options={restaurantList}
                getOptionLabel={(option) => option.label || option}
                value={formData.restaurants}
                onChange={(e, newValue) => {
                  if (e?.target?.dataset?.optionIndex === "0") {
                    if (
                      formData?.restaurants?.length ===
                      restaurantList?.length - 1
                    ) {
                      setFormData((prev) => ({ ...prev, restaurants: [] }));
                      restaurantList[0].label = "Select All";
                      // restaurantList[0].value = "all";
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        restaurants: restaurantList?.filter(
                          (data) => data?.value !== "all"
                        ),
                      }));
                      restaurantList[0].label = "Deselect All";
                      // restaurantList[0].value = "deselect-all";
                    }
                  } else {
                    setFormData((prev) => ({ ...prev, restaurants: newValue }));
                  }
                }}
                renderTags={(selected, getTagProps) => {
                  const visibleTags = selected.slice(0, 3);
                  const extraCount = selected.length - visibleTags?.length;

                  return (
                    <>
                      {visibleTags?.map((option, index) => (
                        <Chip
                          key={index}
                          label={option.label || option}
                          {...getTagProps({ index })}
                          onClick={() => {}}
                          style={{ cursor: "default" }}
                        />
                      ))}
                      {extraCount > 0 && (
                        <Chip
                          label={`+${extraCount} more`}
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setPopoverOpen(true);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                    </>
                  );
                }}
                renderInput={(params) => (
                  <Tooltip arrow
                    title={
                      formData.restaurants.length > 0
                        ? formData.restaurants
                            .map((r) => r.label || r)
                            .join(", ")
                        : ""
                    }
                  >
                    <TextField
                      {...params}
                      label="Select Restaurants"
                      variant="standard"
                    />
                  </Tooltip>
                )}
              />
              {errors?.restaurants && (
                <p className={styles.error}>{errors?.restaurants}</p>
              )}

              <Popover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={() => setPopoverOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <Box sx={{ p: 2, maxWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected Restaurants
                  </Typography>
                  {formData.restaurants.map((restaurant, index) => (
                    <Chip
                      key={restaurant.value || restaurant.label || index}
                      label={restaurant.label || restaurant}
                      onDelete={() => {
                        const updated = [...formData.restaurants];
                        updated.splice(index, 1);
                        setFormData((prev) => ({
                          ...prev,
                          restaurants: updated,
                        }));
                        if (updated.length <= 3) setPopoverOpen(false);
                      }}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              </Popover>
            </Grid>

            {/* Usage Limit */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                fullWidth
                name="usageLimit"
                label="Usage Limit"
                type="number"
                value={formData.usageLimit}
                onChange={handleChange}
                variant="standard"
                InputProps={{
                  inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                  classes: { input: styles.noArrows },
                }}
              />
              {errors?.usageLimit && (
                <p className={styles.error}>{errors?.usageLimit}</p>
              )}
            </Grid>

            {/* Valid From */}
            <Grid xs={12} className={styles.inputGroup}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label="Valid From"
                  format="DD/MM/YYYY"
                  disableOpenPicker={false} // set to true if you only want keyboard input
                  value={formData.validFrom || null}
                  onChange={handleValidFromChange}
                  allowKeyboardControl
                  slotProps={{
                    textField: { variant: "standard", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
              {errors?.validFrom && (
                <p className={styles.error}>{errors.validFrom}</p>
              )}
            </Grid>

            {/* Valid Till */}
            <Grid xs={12} className={styles.inputGroup}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label="Valid Till"
                  format="DD/MM/YYYY"
                  disableOpenPicker={false}
                  value={formData.validTill || null}
                  onChange={handleValidTillChange}
                  allowKeyboardControl
                  slotProps={{
                    textField: { variant: "standard", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
              {errors?.validTill && (
                <p className={styles.error}>{errors.validTill}</p>
              )}
            </Grid>
          </Grid>
          {/* Submit Button */}
          <DialogActions>
            <Button
              disabled={!isFormValid}
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: "#207d46",
              }}
              className={styles.saveButton}
            >
              {isEditing ? "Edit" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Coupons;
